import React, { useState, useEffect } from "react";
import { Container } from "./styles";
import {
  FiShield,
  FiClock,
  FiCheckCircle,
  FiSearch,
  FiCalendar,
} from "react-icons/fi";
import {
  HiOutlineCheckCircle,
  HiOutlineXCircle,
} from "react-icons/hi";
import { GoOrganization } from "react-icons/go";
import api from "../../services/api";
import NotificationModal from "../ecac/NotificationModal";
import TaxMonitoringLoadingSkeleton from "./TaxMonitoringLoadingSkeleton";
import ConfirmModal from "../../components/ConfirmModal";

const STATUS_CONFIG = {
  nao_resolvido: { label: 'Não Resolvido', color: '#f87171', bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.3)', key: 'danger', icon: HiOutlineXCircle },
  em_analise:    { label: 'Em Análise',    color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.12)', border: 'rgba(251, 191, 36, 0.3)', key: 'warn',   icon: FiClock },
  resolvido:     { label: 'Resolvido',     color: '#34d399', bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.3)', key: 'ok',     icon: HiOutlineCheckCircle },
};

const TAG_PALETTE = [
  '#dc2626','#ea580c','#65a30d','#16a34a','#0d9488','#0891b2','#2563eb','#4f46e5','#9333ea','#db2777',
];

const getTagColor = (entity) => {
  const seed = entity?.cnpj || entity?.id || '';
  if (!seed) return TAG_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) { hash = seed.charCodeAt(i) + ((hash << 5) - hash); }
  return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length];
};

export default function TaxMonitoring({ embedded = false }) {
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({ total: 0, importantes: 0, naoLidas: 0, naoResolvidas: 0, emAnalise: 0, resolvidas: 0 });
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [statusModal, setStatusModal] = useState({ open: false, notificacao: null, newStatus: null });

  useEffect(() => { fetchNotificacoes(); fetchKpis(); }, []);

  const fetchNotificacoes = async () => {
    try {
      const projeto = localStorage.getItem('vidal_selected_project');
      const response = await api.get("/ecac/lista", { params: { projeto, limit: 100 } });
      if (response.data.success) setNotificacoes(response.data.notificacoes);
    } catch (e) { console.error("Erro ao buscar notificações:", e); }
    finally { setLoading(false); }
  };

  const fetchKpis = async () => {
    try {
      const projeto = localStorage.getItem('vidal_selected_project');
      const response = await api.get("/ecac/kpis", { params: { projeto } });
      if (response.data.success) setKpis(response.data.kpis);
    } catch (e) { console.error("Erro ao buscar KPIs:", e); }
  };

  const handleViewDetails = async (notificacao) => {
    setSelectedNotification(notificacao);
    setModalOpen(true);
    setLoadingDetails(true);
    try {
      const response = await api.get(`/ecac/detalhes/${notificacao.id}`);
      if (response.data.success) setSelectedNotification(response.data.notificacao);
    } catch (e) { console.error("Erro ao buscar detalhes:", e); }
    finally { setLoadingDetails(false); }
  };

  const handleCloseModal = () => { setModalOpen(false); setSelectedNotification(null); };

  const handleUpdateStatus = async (updates) => {
    if (!selectedNotification) return;
    try {
      await api.patch(`/ecac/status/${selectedNotification.id}`, updates);
      setSelectedNotification(prev => ({ ...prev, ...updates }));
      setNotificacoes(prev => prev.map(n => n.id === selectedNotification.id ? { ...n, ...updates } : n));
      fetchKpis();
    } catch (e) { console.error("Erro ao atualizar status:", e); }
  };

  const handleStatusClick = (e, notificacao) => {
    e.stopPropagation();
    const statusOrder = ['nao_resolvido', 'em_analise', 'resolvido'];
    const currentStatus = notificacao.status || 'nao_resolvido';
    const nextStatus = statusOrder[(statusOrder.indexOf(currentStatus) + 1) % statusOrder.length];
    setStatusModal({ open: true, notificacao, newStatus: nextStatus });
  };

  const handleConfirmStatus = async () => {
    const { notificacao, newStatus } = statusModal;
    if (!notificacao || !newStatus) return;
    try {
      await api.patch(`/ecac/status/${notificacao.id}`, { status: newStatus });
      setNotificacoes(prev => prev.map(n => n.id === notificacao.id ? { ...n, status: newStatus, resolvida: newStatus === 'resolvido' } : n));
      if (selectedNotification?.id === notificacao.id) setSelectedNotification(prev => ({ ...prev, status: newStatus, resolvida: newStatus === 'resolvido' }));
      fetchKpis();
    } catch (e) { console.error("Erro ao atualizar status:", e); }
    finally { setStatusModal({ open: false, notificacao: null, newStatus: null }); }
  };

  const getStatusInfo = (notificacao) => STATUS_CONFIG[notificacao.status || 'nao_resolvido'] || STATUS_CONFIG.nao_resolvido;

  const getTypeFromAssunto = (assunto) => {
    if (assunto.includes("AIIM") || assunto.includes("Auto de Infração")) return "AIIM";
    if (assunto.includes("DCTF")) return "DCTF";
    if (assunto.includes("Malha")) return "Malha Fiscal";
    if (assunto.includes("Crédito")) return "Glosa de Crédito";
    if (assunto.includes("SPED")) return "Obrigação Acessória";
    return "Notificação";
  };

  const calculateDaysLeft = (dataValidade) => {
    if (!dataValidade || dataValidade === '-') return null;
    const [day, month, year] = dataValidade.split('/');
    const diffDays = Math.ceil((new Date(year, month - 1, day) - new Date()) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredIntimations = notificacoes.filter((notificacao) => {
    const status = notificacao.status || 'nao_resolvido';
    const matchesFilter = filter === "all" || (filter === "pending" && status === 'nao_resolvido') || (filter === "em_analise" && status === 'em_analise') || (filter === "resolved" && status === 'resolvido');
    const matchesSearch = !searchTerm || notificacao.assunto?.toLowerCase().includes(searchTerm.toLowerCase()) || notificacao.remetente?.toLowerCase().includes(searchTerm.toLowerCase()) || notificacao.parecer?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    const isDark = document.body.classList.contains('dark-theme') || document.documentElement.getAttribute('data-theme') === 'dark' || window.matchMedia('(prefers-color-scheme: dark)').matches;
    return <TaxMonitoringLoadingSkeleton isDark={isDark} />;
  }

  return (
    <Container>
      {!embedded && (
        <div className="header-info">
          <div className="header-details">
            <h3>e-CAC - Centro de Atendimento Virtual ao Contribuinte</h3>
            <div className="header-meta">
              <span className="status-tag">Sistema Ativo</span>
              <span className="update-tag">{kpis.total} notificações monitoradas</span>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="kpi-cards">
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb"><FiShield /></div>
            <div className="text">
              <p>{kpis.total}</p>
              <p>Total</p>
            </div>
          </div>
        </div>
        <div className="kpi-card danger">
          <div className="card-content">
            <div className="thumb"><HiOutlineXCircle /></div>
            <div className="text">
              <p>{kpis.naoResolvidas}</p>
              <p>Não Resolvidas</p>
            </div>
          </div>
        </div>
        <div className="kpi-card warning">
          <div className="card-content">
            <div className="thumb"><FiClock /></div>
            <div className="text">
              <p>{kpis.emAnalise || 0}</p>
              <p>Em Análise</p>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb"><HiOutlineCheckCircle /></div>
            <div className="text">
              <p>{kpis.resolvidas}</p>
              <p>Resolvidas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="search-box">
          <FiSearch />
          <input type="text" placeholder="Buscar intimações..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="toolbar-right">
          <button className={`filter-pill ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Todas</button>
          <button className={`filter-pill ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>Não Resolvidas</button>
          <button className={`filter-pill ${filter === 'em_analise' ? 'active' : ''}`} onClick={() => setFilter('em_analise')}>Em Análise</button>
          <button className={`filter-pill ${filter === 'resolved' ? 'active' : ''}`} onClick={() => setFilter('resolved')}>Resolvidas</button>
        </div>
      </div>

      {/* List */}
      <div className="notif-grid">
        {filteredIntimations.length > 0 ? (
          filteredIntimations.map((notificacao) => {
            const daysLeft = calculateDaysLeft(notificacao.dataValidade);
            const statusInfo = getStatusInfo(notificacao);
            const StatusIcon = statusInfo.icon;
            return (
              <div
                key={notificacao.id}
                className={`notif-card ${statusInfo.key}`}
                onClick={() => handleViewDetails(notificacao)}
                style={{ opacity: notificacao.status === 'resolvido' ? 0.7 : 1 }}
              >
                <div className="notif-icon" style={{ background: statusInfo.bg, border: `1px solid ${statusInfo.border}` }}>
                  <StatusIcon style={{ color: statusInfo.color }} />
                </div>
                <div className="notif-info">
                  <h4 className="notif-title">{notificacao.assunto}</h4>
                  <div className="notif-meta">
                    <span><GoOrganization /> CNPJ: {notificacao.cnpj}</span>
                    <span><GoOrganization /> {notificacao.remetente}</span>
                    {daysLeft !== null && <span><FiCalendar /> Prazo: {notificacao.dataValidade} ({daysLeft} dias)</span>}
                  </div>
                  <div className="notif-tags">
                    <span className={`status-pill ${statusInfo.key}`}>{statusInfo.label}</span>
                    {notificacao.protocolo && <span className="status-pill ok">Protocolo: {notificacao.protocolo}</span>}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            <div className="empty-icon"><FiCheckCircle /></div>
            <h4>Nenhuma notificação encontrada</h4>
            <p>Todas as notificações do e-CAC estão em dia ou os filtros aplicados não retornaram resultados.</p>
          </div>
        )}
      </div>

      {modalOpen && selectedNotification && (
        <NotificationModal notification={selectedNotification} loading={loadingDetails} onClose={handleCloseModal} onUpdateStatus={handleUpdateStatus} />
      )}

      <ConfirmModal
        isOpen={statusModal.open}
        onClose={() => setStatusModal({ open: false, notificacao: null, newStatus: null })}
        onConfirm={handleConfirmStatus}
        title="Alterar Status"
        message={`Deseja alterar o status desta notificação para "${statusModal.newStatus ? STATUS_CONFIG[statusModal.newStatus]?.label : ''}"?`}
        warning="Esta ação irá atualizar o status da notificação no sistema."
        variant="warning"
        confirmText="Confirmar"
        cancelText="Cancelar"
        confirmVariant="primary"
      />
    </Container>
  );
}
